<template>
  <b-container fluid class="pt-3">
    <b-row>
      <!-- Left column: Tags list -->
      <b-col cols="6" class="border-right">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>Tags</strong>
          <b-button-group size="sm" v-if="!showNewTagInput">
            <b-button variant="outline-primary" @click="showNewTagInput = true">+ Tag</b-button>
            <b-button variant="outline-danger" :disabled="!selectedTag" @click="deleteTag(selectedTag)">- Tag</b-button>
          </b-button-group>
        </div>
        <b-input-group v-if="showNewTagInput" class="mb-2" size="sm">
          <b-form-input v-model="newTagName" placeholder="Tag name" @keyup.enter="createTag" autofocus></b-form-input>
          <b-input-group-append>
            <b-button variant="primary" @click="createTag">Add</b-button>
            <b-button variant="outline-secondary" @click="showNewTagInput = false; newTagName = ''">✕</b-button>
          </b-input-group-append>
        </b-input-group>

        <b-list-group style="max-height: 33vh; overflow-y: auto;">
          <b-list-group-item
            v-for="tag in tags"
            :key="tag.tagId"
            :id="'group-tag-' + tag.tagId"
            :active="selectedTag && selectedTag.tagId === tag.tagId"
            :variant="isTagDeleted(tag) ? 'danger' : ''"
            button
            class="d-flex justify-content-between align-items-center py-2"
            @click="selectTag(tag)"
            @dblclick.prevent="startEditTag(tag)"
            @mousemove="onTagHover($event, tag)"
            @mouseleave="hoverTag = null"
          >
            <b-form-input
              v-if="editingTag && editingTag.tagId === tag.tagId"
              v-model="editingTagName"
              size="sm"
              class="mr-2"
              @keyup.enter="commitEditTag(tag)"
              @keyup.esc="editingTag = null"
              @blur="commitEditTag(tag)"
              @click.stop
              autofocus
            ></b-form-input>
            <span v-else :class="{ 'text-decoration-line-through': isTagDeleted(tag) }">
              <span v-if="tagIsBooked(tag)" class="tag-booked-label">{{ getPendingTagName(tag) }}</span>
              <template v-else>{{ getPendingTagName(tag) }}</template>
            </span>
            <b-badge :variant="selectedTag && selectedTag.tagId === tag.tagId ? 'light' : 'secondary'" pill>
              {{ tagDisplayCount(tag) }}
            </b-badge>
            <div
              v-if="!(editingTag && editingTag.tagId === tag.tagId) && hoverTag && hoverTag.tagId === tag.tagId"
              class="tag-hover-popup"
              :style="{ top: hoverY + 'px', left: hoverX + 'px' }"
            >
              <div v-for="d in getTagDisplays(tag)" :key="d.displayId">{{ d.name }}</div>
              <div v-if="getTagDisplays(tag).length === 0" class="text-muted">No displays</div>
            </div>
          </b-list-group-item>
          <b-list-group-item v-if="tags.length === 0" disabled class="text-muted">
            No tags yet
          </b-list-group-item>
        </b-list-group>
        <small class="text-muted mt-2 d-block">
          <b-icon icon="exclamation-circle" variant="warning"></b-icon>
          Tags with active bookings (shown in blue) — please clear their bookings in IPAM before making changes.
        </small>
        <small class="text-muted mt-1 d-block">
          <b-icon icon="info-circle" variant="info"></b-icon>
          Each tag must have at least one display assigned.
        </small>
      </b-col>

      <!-- Right column: Displays for selected tag -->
      <b-col cols="6">
        <template v-if="selectedTag && !isTagDeleted(selectedTag)">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <strong>Current Tag: <b-badge variant="info">{{ getPendingTagName(selectedTag) }}</b-badge></strong>
            <div class="d-flex align-items-center">
              <b-form-input v-model="displaySearch" placeholder="Search displays..." size="sm" class="mr-2" style="width: 160px;"></b-form-input>
              <b-button size="sm" variant="outline-secondary" @click="toggleSelectAll">
                {{ allSelected ? 'Cancel All' : 'Select All' }}
              </b-button>
            </div>
          </div>
          <b-list-group style="max-height: 33vh; overflow-y: auto;">
            <b-list-group-item
              v-for="d in filteredAllDisplays"
              :key="d.displayId"
              class="d-flex justify-content-between align-items-center py-2"
            >
              <span>{{ d.name }}</span>
              <b-form-checkbox
                :checked="isInTag(d)"
                @change="toggleDisplay(d, $event)"
                switch
              ></b-form-checkbox>
            </b-list-group-item>
            <b-list-group-item v-if="filteredAllDisplays.length === 0" disabled class="text-muted">
              No displays found
            </b-list-group-item>
          </b-list-group>
        </template>
        <div v-else class="text-muted pt-4 text-center">
          Select a tag on the left to manage its displays
        </div>
      </b-col>
    </b-row>

    <!-- Bottom action bar -->
    <b-row class="mt-4 border-top pt-3">
      <b-col class="d-flex justify-content-between align-items-center">
        <b-button-group>
          <b-button variant="outline-danger" @click="clearAllTags">Clear All Tags</b-button>
          <b-button variant="outline-secondary" @click="showDetails = !showDetails">
            Details {{ showDetails ? '▲' : '▼' }}
          </b-button>
        </b-button-group>
        <b-button-group>
          <b-button variant="outline-secondary" :disabled="!hasPendingChanges" @click="discardChanges">Discard</b-button>
          <b-button variant="primary" :disabled="!hasPendingChanges || applying" @click="applyChanges">
            Apply <b-badge v-if="pendingCount > 0" variant="light">{{ pendingCount }}</b-badge>
          </b-button>
        </b-button-group>
      </b-col>
    </b-row>

    <!-- Details table -->
    <b-row v-if="showDetails" class="mt-3">
      <b-col>
        <div class="details-scroll-wrap" ref="detailsWrap">
          <table class="details-table table table-bordered table-sm" :style="{ width: tags.length * 12 + 'em' }">
            <colgroup>
              <col v-for="tag in tags" :key="tag.tagId" style="width: 12em; min-width: 12em;">
            </colgroup>
            <thead>
              <tr>
                <th v-for="tag in tags" :key="tag.tagId">
                  {{ getPendingTagName(tag) }}
                  <b-badge variant="secondary" pill class="ml-1">{{ tagDisplayCount(tag) }}</b-badge>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in detailsRowCount" :key="i">
                <td v-for="tag in tags" :key="tag.tagId">
                  {{ detailsRows[tag.tagId] && detailsRows[tag.tagId][i - 1] ? detailsRows[tag.tagId][i - 1].name : '' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
export default {
  props: {
    display: {
      type: Object,
      required: true
    },
    activeTeam: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      allDisplays: [],
      tags: [],
      selectedTag: null,
      tagMemberships: {},
      displaySearch: '',
      showNewTagInput: false,
      newTagName: '',
      editingTag: null,
      editingTagName: '',
      // pending changes
      pendingToAdd: {},    // { tagId: { displayId: true } }
      pendingToRemove: {}, // { tagId: { displayId: true } }
      pendingRenames: {},  // { tagId: newName }
      pendingDeletes: new Set(), // Set(tagId)
      pendingNewTags: [],  // [{ name, displayIds: Set }]
      clearAll: false,
      applying: false,
      showDetails: false,
      hoverTag: null,
      hoverX: 0,
      hoverY: 0,
    }
  },
  computed: {
    detailsRows () {
      const rows = {}
      for (let tag of this.tags) {
        rows[tag.tagId] = this.getTagDisplays(tag)
      }
      return rows
    },
    detailsRowCount () {
      return Math.max(0, ...Object.values(this.detailsRows).map(d => d.length))
    },
    allSelected () {
      if (!this.selectedTag || this.filteredAllDisplays.length === 0) return false
      const tid = this.selectedTag.tagId
      const base = this.tagMemberships[tid] || {}
      const added = this.pendingToAdd[tid] || {}
      const removed = this.pendingToRemove[tid] || {}
      return this.filteredAllDisplays.every(d => {
        const did = d.displayId
        if (this.clearAll) return !!added[did]
        return !!added[did] || (!removed[did] && !!base[did])
      })
    },
    filteredAllDisplays () {
      const q = this.displaySearch.toLowerCase()
      return this.allDisplays
        .filter(d => d.name.toLowerCase().includes(q))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    effectiveAdds () {
      // only count adds that aren't already in the server-side membership
      const result = {}
      for (let [tid, displayMap] of Object.entries(this.pendingToAdd)) {
        const tag = this.tags.find(t => String(t.tagId) === String(tid))
        for (let did of Object.keys(displayMap)) {
          if (tag && !tag._isNew && this.tagMemberships[tid] && this.tagMemberships[tid][did]) continue
          if (!result[tid]) result[tid] = {}
          result[tid][did] = true
        }
      }
      return result
    },
    effectiveRemoves () {
      // only count removes that are actually in the server-side membership
      const result = {}
      for (let [tid, displayMap] of Object.entries(this.pendingToRemove)) {
        for (let did of Object.keys(displayMap)) {
          if (!this.tagMemberships[tid] || !this.tagMemberships[tid][did]) continue
          if (!result[tid]) result[tid] = {}
          result[tid][did] = true
        }
      }
      return result
    },
    hasPendingChanges () {
      return this.clearAll ||
        Object.keys(this.effectiveAdds).some(k => Object.keys(this.effectiveAdds[k]).length > 0) ||
        Object.keys(this.effectiveRemoves).some(k => Object.keys(this.effectiveRemoves[k]).length > 0) ||
        Object.keys(this.pendingRenames).length > 0 ||
        this.pendingDeletes.size > 0 ||
        this.pendingNewTags.length > 0
    },
    pendingCount () {
      let n = 0
      if (this.clearAll) return 1
      Object.values(this.effectiveAdds).forEach(o => n += Object.keys(o).length)
      Object.values(this.effectiveRemoves).forEach(o => n += Object.keys(o).length)
      n += Object.keys(this.pendingRenames).length
      n += this.pendingDeletes.size
      n += this.pendingNewTags.length
      return n
    },
  },
  watch: {
    showDetails (val) {
      if (val) this.$nextTick(this.updateDetailsHeight)
    }
  },
  mounted () {
    this.fetchAll()
  },
  methods: {
    onTagHover (e, tag) {
      this.hoverTag = tag
      this.hoverX = e.clientX + 12
      this.hoverY = e.clientY + 8
    },
    updateDetailsHeight () {
      const el = this.$refs.detailsWrap
      if (!el) return
      const top = el.getBoundingClientRect().top
      el.style.maxHeight = (window.innerHeight - top - 16) + 'px'
    },
    async fetchAll (deletedIds = new Set()) {
      await this.fetchDisplays()
      this.buildTagIndex(deletedIds)
    },
    async fetchDisplays () {
      const res = await this.$axios.get('v1/displays?include=tags,bookings&limit=all&team=' + parseInt(this.activeTeam.id))
      this.allDisplays = res.data.data
    },
    buildTagIndex (deletedIds = new Set()) {
      const prevTags = this.tags.filter(t => !t._isNew)
      const tagMap = {}
      const memberships = {}
      for (let d of this.allDisplays) {
        const tagList = d.tags && d.tags.data ? d.tags.data : []
        for (let t of tagList) {
          const id = t.tagId || t.id
          const name = t.tag || t.name
          if (!tagMap[id]) {
            tagMap[id] = { tagId: id, name, displayIds: new Set() }
          }
          tagMap[id].displayIds.add(d.displayId)
          if (!memberships[id]) memberships[id] = {}
          memberships[id][d.displayId] = t.tagId || t.id
        }
      }
      // preserve tags with 0 displays, but not ones that were just deleted/renamed
      const deletedStrIds = new Set([...deletedIds].map(String))
      for (let pt of prevTags) {
        if (!tagMap[pt.tagId] && !deletedStrIds.has(String(pt.tagId))) {
          tagMap[pt.tagId] = { tagId: pt.tagId, name: pt.name, displayIds: new Set() }
        }
      }
      this.tags = Object.values(tagMap).sort((a, b) => a.name.localeCompare(b.name))
      this.tagMemberships = memberships
      if (this.selectedTag) {
        this.selectedTag = this.tags.find(t => t.tagId === this.selectedTag.tagId) || null
      }
    },
    resetPending () {
      this.pendingToAdd = {}
      this.pendingToRemove = {}
      this.pendingRenames = {}
      this.pendingDeletes = new Set()
      this.pendingNewTags = []
      this.clearAll = false
      this.tags = this.tags.filter(t => !t._isNew)
    },
    discardChanges () {
      this.resetPending()
      this.selectedTag = null
    },

    // --- local state helpers ---
    isTagDeleted (tag) {
      return this.pendingDeletes.has(tag.tagId)
    },
    getPendingTagName (tag) {
      return this.pendingRenames[tag.tagId] || tag.name
    },
    isInTag (display) {
      if (!this.selectedTag) return false
      const tid = this.selectedTag.tagId
      const added = !!(this.pendingToAdd[tid] && this.pendingToAdd[tid][display.displayId])
      const removed = !!(this.pendingToRemove[tid] && this.pendingToRemove[tid][display.displayId])
      const base = !!(this.tagMemberships[tid] && this.tagMemberships[tid][display.displayId])
      if (this.clearAll) return added
      return added ? true : removed ? false : base
    },
    tagDisplayCount (tag) {
      const tid = tag.tagId
      if (this.clearAll) {
        return this.pendingToAdd[tid] ? Object.keys(this.pendingToAdd[tid]).length : 0
      }
      const baseIds = this.tagMemberships[tid] || {}
      const addedNew = this.pendingToAdd[tid]
        ? Object.keys(this.pendingToAdd[tid]).filter(did => !baseIds[did]).length
        : 0
      const removed = this.pendingToRemove[tid]
        ? Object.keys(this.pendingToRemove[tid]).filter(did => !!baseIds[did]).length
        : 0
      const base = tag.displayIds ? tag.displayIds.size : 0
      return base + addedNew - removed
    },
    tagIsBooked (tag) {
      if (tag._isNew) return false
      const tid = tag.tagId
      const today = new Date().toISOString().slice(0, 10)
      const memberMap = this.tagMemberships[tid] || {}
      for (let did of Object.keys(memberMap)) {
        const display = this.allDisplays.find(d => d.displayId === parseInt(did))
        if (!display || !display.bookings) continue
        const hasBooking = display.bookings.data.some(b => b.tagId != null && b.tagId == tid && b.endDate >= today)
        if (hasBooking) return true
      }
      return false
    },
    getTagDisplays (tag) {
      const tid = tag.tagId
      const base = this.allDisplays.filter(d =>
        !!(this.tagMemberships[tid] && this.tagMemberships[tid][d.displayId]) &&
        !(this.pendingToRemove[tid] && this.pendingToRemove[tid][d.displayId])
      )
      const addedIds = this.pendingToAdd[tid] ? Object.keys(this.pendingToAdd[tid]).map(Number) : []
      const added = this.allDisplays.filter(d =>
        addedIds.includes(d.displayId) && !(this.tagMemberships[tid] && this.tagMemberships[tid][d.displayId])
      )
      return [...base, ...added].sort((a, b) => a.name.localeCompare(b.name))
    },

    // --- user actions (local only) ---
    selectTag (tag) {
      if (this.isTagDeleted(tag)) return
      if (this.selectedTag && this.selectedTag.tagId === tag.tagId) {
        this.selectedTag = null
        return
      }
      this.selectedTag = tag
      this.displaySearch = ''
    },
    toggleSelectAll () {
      if (this.allSelected) {
        for (let d of this.filteredAllDisplays) {
          if (this.isInTag(d)) this.toggleDisplay(d, false)
        }
      } else {
        for (let d of this.filteredAllDisplays) {
          if (!this.isInTag(d)) this.toggleDisplay(d, true)
        }
      }
    },
    toggleDisplay (display, checked) {
      const tid = this.selectedTag.tagId
      if (!this.pendingToAdd[tid]) this.$set(this.pendingToAdd, tid, {})
      if (!this.pendingToRemove[tid]) this.$set(this.pendingToRemove, tid, {})
      if (checked) {
        this.$set(this.pendingToAdd[tid], display.displayId, true)
        this.$delete(this.pendingToRemove[tid], display.displayId)
      } else {
        this.$set(this.pendingToRemove[tid], display.displayId, true)
        this.$delete(this.pendingToAdd[tid], display.displayId)
      }
    },
    startEditTag (tag) {
      if (this.isTagDeleted(tag)) return
      this.editingTag = tag
      this.editingTagName = this.getPendingTagName(tag)
    },
    commitEditTag (tag) {
      if (!this.editingTag) return
      this.editingTag = null
      const newName = this.editingTagName.trim()
      if (!newName || newName === this.getPendingTagName(tag)) return
      this.$set(this.pendingRenames, tag.tagId, newName)
    },
    deleteTag (tag) {
      this.pendingDeletes.add(tag.tagId)
      this.$set(this, 'pendingDeletes', new Set(this.pendingDeletes))
      if (this.selectedTag && this.selectedTag.tagId === tag.tagId) this.selectedTag = null
    },
    createTag () {
      const name = this.newTagName.trim()
      if (!name) return
      this.showNewTagInput = false
      this.newTagName = ''
      if (this.tags.find(t => t.name === name) || this.pendingNewTags.find(t => t.name === name)) return
      const tempId = 'new_' + Date.now()
      const newTag = { tagId: tempId, name, displayIds: new Set(), _isNew: true }
      this.pendingNewTags.push(newTag)
      this.tags = [...this.tags, newTag]
      this.selectTag(newTag)
    },
    clearAllTags () {
      this.clearAll = true
      this.pendingToAdd = {}
      this.pendingToRemove = {}
      this.pendingRenames = {}
      this.pendingDeletes = new Set()
      this.pendingNewTags = []
      this.selectedTag = null
    },

    // --- apply ---
    getAffectedBookedTags () {
      const affected = []
      const check = (tid) => {
        const tag = this.tags.find(t => String(t.tagId) === String(tid))
        if (tag && this.tagIsBooked(tag) && !affected.find(t => t.tagId === tag.tagId)) {
          affected.push(tag)
        }
      }
      if (this.clearAll) {
        this.tags.forEach(t => { if (this.tagIsBooked(t)) affected.push(t) })
      } else {
        for (let tid of this.pendingDeletes) check(tid)
        for (let tid of Object.keys(this.pendingRenames)) check(tid)
        for (let tid of Object.keys(this.pendingToAdd)) check(tid)
        for (let tid of Object.keys(this.pendingToRemove)) check(tid)
      }
      return affected
    },
    async applyChanges () {
      if (this.applying) return
      this.applying = true
      try {
        // warn about any tag (new or existing) that would end up with 0 displays
        const emptyTags = this.tags.filter(tag => {
          if (this.isTagDeleted(tag)) return false
          return this.tagDisplayCount(tag) === 0
        })
        if (emptyTags.length > 0) {
          const names = emptyTags.map(t => `"${this.getPendingTagName(t)}"`).join(', ')
          await this.$bvModal.msgBoxOk(
            `The following tag(s) would have no displays: ${names}. Please assign at least one display to each, or remove them.`,
            { title: 'Empty Tags', centered: true }
          )
          return
        }

        const bookedAffected = this.getAffectedBookedTags()
        if (bookedAffected.length > 0) {
          const names = bookedAffected.map(t => `"${this.getPendingTagName(t)}"`).join(', ')
          const ok = await this.$bvModal.msgBoxConfirm(
            `The following tag(s) have active bookings: ${names}. Continuing will remove all campaigns currently booked to these tags in IPAM. Are you sure?`,
            { title: 'Warning: Active Bookings', okVariant: 'danger', okTitle: 'Yes, continue', cancelTitle: 'Cancel', centered: true }
          )
          if (!ok) return
          // clear all bookings for affected tags before proceeding
          const today = new Date().toISOString().slice(0, 10)
          const clearOps = []
          for (let tag of bookedAffected) {
            const memberMap = this.tagMemberships[tag.tagId] || {}
            for (let did of Object.keys(memberMap)) {
              const display = this.allDisplays.find(d => d.displayId === parseInt(did))
              if (!display || !display.bookings) continue
              for (let b of display.bookings.data) {
                if (b.tagId != null && b.tagId == tag.tagId && b.endDate >= today) {
                  clearOps.push(this.$axios.delete('v1/displays/' + display.displayId + '/campaigns/' + b.id))
                }
              }
            }
          }
          if (clearOps.length > 0) await Promise.all(clearOps)
        }

        const ops = []

        if (this.clearAll) {
          for (let d of this.allDisplays) {
            const tagList = d.tags && d.tags.data ? d.tags.data : []
            for (let t of tagList) {
              ops.push(this.$axios.delete('v1/displays/' + d.displayId + '/tags/' + (t.tagId || t.id)))
            }
          }
          await Promise.all(ops)
          await this.fetchAll(new Set(this.tags.map(t => t.tagId)))
          this.$notifications.add({ title: 'Saved', type: 'success', body: 'All tags cleared.', timeout: true })
          this.resetPending()
          return
        }

        // deletes
        for (let tid of this.pendingDeletes) {
          const memberMap = this.tagMemberships[tid] || {}
          for (let did of Object.keys(memberMap)) {
            ops.push(this.$axios.delete('v1/displays/' + did + '/tags/' + tid))
          }
        }

        // removes
        for (let [tid, displayMap] of Object.entries(this.pendingToRemove)) {
          for (let did of Object.keys(displayMap)) {
            if (!this.tagMemberships[tid] || !this.tagMemberships[tid][did]) continue
            ops.push(this.$axios.delete('v1/displays/' + parseInt(did) + '/tags/' + parseInt(tid)))
          }
        }

        // adds for existing tags (skip displays already in the tag on the server)
        for (let [tid, displayMap] of Object.entries(this.pendingToAdd)) {
          const tag = this.tags.find(t => String(t.tagId) === String(tid))
          if (!tag || tag._isNew) continue
          const tagName = this.pendingRenames[tid] || tag.name
          for (let did of Object.keys(displayMap)) {
            if (this.tagMemberships[tid] && this.tagMemberships[tid][did]) continue
            ops.push(this.$axios.post('v1/displays/' + parseInt(did) + '/tags', { tag: tagName }))
          }
        }

        // renames: serial per tag — first display POST creates the new tagId, rest reuse same name
        for (let [tid, newName] of Object.entries(this.pendingRenames)) {
          const tag = this.tags.find(t => String(t.tagId) === String(tid))
          if (!tag) continue
          const memberMap = this.tagMemberships[tid] || {}
          const displayIds = Object.keys(memberMap).map(Number)
          if (displayIds.length === 0) continue
          await Promise.all(displayIds.map(did =>
            this.$axios.delete('v1/displays/' + did + '/tags/' + tid)
          ))
          await this.$axios.post('v1/displays/' + displayIds[0] + '/tags', { tag: newName })
          if (displayIds.length > 1) {
            await Promise.all(displayIds.slice(1).map(did =>
              this.$axios.post('v1/displays/' + did + '/tags', { tag: newName })
            ))
          }
        }

        // new tags: serial — first display creates the tag, remaining displays reuse the same name
        for (let nt of this.pendingNewTags) {
          const displayIds = Object.keys(this.pendingToAdd[nt.tagId] || {}).map(Number)
          if (displayIds.length === 0) continue
          await this.$axios.post('v1/displays/' + displayIds[0] + '/tags', { tag: nt.name })
          if (displayIds.length > 1) {
            await Promise.all(displayIds.slice(1).map(did =>
              this.$axios.post('v1/displays/' + did + '/tags', { tag: nt.name })
            ))
          }
        }

        const deletedIds = new Set([
          ...this.pendingDeletes,
          ...Object.keys(this.pendingRenames).map(Number)
        ])
        await Promise.all(ops)
        await this.fetchAll(deletedIds)
        this.$notifications.add({ title: 'Saved', type: 'success', body: 'Tag changes applied.', timeout: true })
        this.resetPending()
      } catch (e) {
        this.$notifications.add({ title: 'Error', type: 'danger', body: e?.response?.data?.message || e.message || 'Apply failed', timeout: true })
      } finally {
        this.applying = false
      }
    },
  }
}
</script>

<style scoped>
.text-decoration-line-through {
  text-decoration: line-through;
}

.tag-hover-popup {
  position: fixed;
  z-index: 9999;
  background: white;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 0.85em;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  pointer-events: none;
  max-width: 200px;
}

.tag-booked-label {
  background-color: #cce5ff;
  color: #004085;
  border-radius: 3px;
  padding: 1px 5px;
}

.details-scroll-wrap {
  overflow-x: auto;
  overflow-y: auto;
}

.details-table {
  table-layout: fixed;
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
}

.details-table th {
  width: 12em;
  min-width: 12em;
  max-width: 12em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #f2f2f2;
  font-size: 80%;
  color: #555555;
}

.details-table td {
  width: 12em;
  min-width: 12em;
  max-width: 12em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: #fffde7;
  font-size: 80%;
  color: #555555;
}
</style>
